<template>
  <Bar
    :chart-options="computedOptions"
    :chart-data="chartData"
    :chart-id="chartId"
    :dataset-id-key="datasetIdKey"
    :plugins="chartPlugins"
    :css-classes="cssClasses"
    :styles="myStyles"
    @chart:render="onChartRender"
  />
</template>

<script>
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  LinearScale,
  CategoryScale,
  Filler,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  LinearScale,
  CategoryScale,
  Filler,
  zoomPlugin,
);

export default {
  name: "EnhancedBarChart",
  components: {
    Bar,
  },
  props: {
    chartData: {
      type: Object,
      default: () => ({ labels: [], datasets: [] }),
    },
    chartId: {
      type: String,
      default: "enhanced-bar-chart",
    },
    chartOptions: {
      type: Object,
      default: () => ({}),
    },
    datasetIdKey: {
      type: String,
      default: "label",
    },
    cssClasses: {
      default: "",
      type: String,
    },
    styles: {
      type: Object,
      default: () => ({}),
    },
    plugins: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["point-click", "chart-hover"],
  computed: {
    myStyles() {
      return {
        height: "500px",
        position: "relative",
        ...this.styles,
      };
    },

    chartPlugins() {
      return [
        ...this.plugins,
        zoomPlugin,
        {
          id: "performance-zones",
          beforeDraw: (chart) => {
            this.drawPerformanceZones(chart);
          },
        },
        {
          id: "trend-indicators",
          afterDatasetsDraw: (chart) => {
            this.drawTrendIndicators(chart);
          },
        },
        {
          id: "animated-bars",
          beforeDatasetsDraw: (chart) => {
            this.animateBars(chart);
          },
        },
      ];
    },

    computedOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top",
            labels: {
              usePointStyle: true,
              padding: 20,
              font: {
                size: 12,
                family: "Inter, sans-serif",
              },
              generateLabels: (chart) => {
                return this.generateCustomLabels(chart);
              },
            },
          },
          tooltip: {
            enabled: true,
            mode: "index",
            intersect: false,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "white",
            bodyColor: "white",
            borderColor: "rgba(255, 255, 255, 0.1)",
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: true,
            callbacks: {
              title: (context) => {
                return `Period: ${context[0].label}`;
              },
              label: (context) => {
                const unit = this.getDatasetUnit(context.dataset.label);
                return `${context.dataset.label}: ${context.parsed.y}${unit}`;
              },
              afterBody: (context) => {
                return this.getTooltipInsights(context);
              },
              footer: (context) => {
                return this.getTooltipTrend(context);
              },
            },
          },
          zoom: {
            limits: {
              x: { min: "original", max: "original" },
              y: { min: "original", max: "original" },
            },
            pan: {
              enabled: true,
              mode: "x",
              threshold: 10,
            },
            zoom: {
              wheel: {
                enabled: true,
                speed: 0.1,
              },
              pinch: {
                enabled: true,
              },
              mode: "x",
            },
          },
        },
        scales: {
          y: {
            beginAtZero: false,
            grid: {
              color: "rgba(0,0,0,0.1)",
              drawBorder: false,
            },
            title: {
              display: true,
              text: this.getYAxisLabel(),
              font: {
                size: 14,
                weight: "600",
              },
            },
            ticks: {
              callback: (value) => {
                return value + this.getCurrentUnit();
              },
              font: {
                size: 11,
              },
            },
          },
          x: {
            grid: {
              display: false,
            },
            title: {
              display: true,
              text: "Time Period",
              font: {
                size: 14,
                weight: "600",
              },
            },
            ticks: {
              font: {
                size: 11,
              },
              maxRotation: 45,
              minRotation: 0,
            },
          },
        },
        interaction: {
          mode: "nearest",
          axis: "x",
          intersect: false,
        },
        onClick: (event, elements) => {
          if (elements.length > 0) {
            const element = elements[0];
            this.$emit("point-click", element.index, element.datasetIndex);
          }
        },
        onHover: (event, elements) => {
          this.$emit("chart-hover", event, elements);
        },
        animation: {
          duration: 1200,
          easing: "easeInOutQuart",
          delay: (context) => {
            // Stagger animation for each bar
            return context.dataIndex * 100;
          },
          onComplete: () => {
            // Trigger completion event
            this.$emit("animation-complete");
          },
        },
        elements: {
          bar: {
            borderWidth: 2,
            borderRadius: 4,
            borderSkipped: false,
          },
        },
        ...this.chartOptions,
      };
    },
  },

  methods: {
    onChartRender() {
      // Chart has been rendered
      this.$emit("chart:render");
    },

    drawPerformanceZones(chart) {
      const ctx = chart.ctx;
      const chartArea = chart.chartArea;

      // Define performance zones based on chart type
      const zones = this.getPerformanceZones();

      if (!zones || zones.length === 0) return;

      zones.forEach((zone) => {
        const yMin = chart.scales.y.getPixelForValue(zone.yMin);
        const yMax = chart.scales.y.getPixelForValue(zone.yMax);

        ctx.save();
        ctx.fillStyle = zone.backgroundColor;
        ctx.fillRect(
          chartArea.left,
          Math.min(yMin, yMax),
          chartArea.right - chartArea.left,
          Math.abs(yMax - yMin),
        );
        ctx.restore();
      });
    },

    drawTrendIndicators(chart) {
      const ctx = chart.ctx;

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        if (dataset.showTrend === false) return;

        const data = dataset.data.filter(
          (value) => value !== null && value !== undefined,
        );
        if (data.length < 2) return;

        const trend = this.calculateSimpleTrend(data);
        const meta = chart.getDatasetMeta(datasetIndex);
        const bars = meta.data;

        if (bars.length < 2) return;

        // Draw trend arrow above the latest bar (first bar since chart is rendered latest to oldest)
        const latestBar = bars[0];
        if (!latestBar) return;

        ctx.save();
        ctx.font = "12px Inter, sans-serif";
        ctx.textAlign = "center";

        // Determine trend color and symbol
        let color, symbol;
        if (Math.abs(trend) < 1) {
          color = "#6c757d";
          symbol = "→";
        } else if (trend > 0) {
          color = "#28a745";
          symbol = "↗";
        } else {
          color = "#dc3545";
          symbol = "↘";
        }

        ctx.fillStyle = color;
        ctx.fillText(
          `${symbol} ${Math.abs(trend).toFixed(1)}%`,
          latestBar.x,
          latestBar.y - 15,
        );

        ctx.restore();
      });
    },

    animateBars(/* chart */) {
      // Additional custom animations can be added here
      // This is called before each dataset draw
    },

    getPerformanceZones() {
      // Define performance zones for different metrics
      // This could be customized based on metric type
      return null; // Disabled for now
    },

    generateCustomLabels(chart) {
      const original = ChartJS.defaults.plugins.legend.labels.generateLabels;
      const labels = original.call(this, chart);

      // Customize labels to show additional information
      return labels.map((label) => {
        const dataset = chart.data.datasets[label.datasetIndex];

        // Add trend indicator to label
        if (dataset.data && dataset.data.length > 1) {
          const trend = this.calculateSimpleTrend(dataset.data);
          const trendIcon = trend > 0 ? " ↗" : trend < 0 ? " ↘" : " →";
          label.text += trendIcon;
        }

        return label;
      });
    },

    calculateSimpleTrend(data) {
      const validData = data.filter(
        (value) => value !== null && value !== undefined,
      );
      if (validData.length < 2) return 0;

      const first = validData[0];
      const last = validData[validData.length - 1];

      if (first === 0) return 0;
      return ((last - first) / first) * 100;
    },

    getDatasetUnit(label) {
      const unitMap = {
        "10m Sprint": "s",
        "30m Sprint": "s",
        "Countermovement Jump": "cm",
        "Reactive Strength Index": "",
        "Fitness Test": "",
        Height: "cm",
        Weight: "kg",
        "Team Average": "s",
      };
      return unitMap[label] || "";
    },

    getCurrentUnit() {
      // This should be passed from parent or determined from context
      return "";
    },

    getYAxisLabel() {
      // This should be passed from parent or determined from context
      return "Performance Value";
    },

    getTooltipInsights(context) {
      const insights = [];

      if (context.length > 0) {
        const dataIndex = context[0].dataIndex;

        // Add comparison with previous period
        if (dataIndex > 0) {
          const current = context[0].parsed.y;
          const previous =
            context[0].chart.data.datasets[context[0].datasetIndex].data[
              dataIndex - 1
            ];

          if (previous !== null && previous !== undefined) {
            const change = current - previous;
            const percentage = ((change / previous) * 100).toFixed(1);

            if (Math.abs(change) > 0.01) {
              const direction = change > 0 ? "increase" : "decrease";
              insights.push(
                `${Math.abs(percentage)}% ${direction} from previous period`,
              );
            }
          }
        }

        // Add session count for aggregated data
        insights.push("Click for detailed analysis");
      }

      return insights;
    },

    getTooltipTrend(context) {
      if (context.length > 0) {
        const dataset = context[0].dataset;
        if (dataset.data && dataset.data.length > 2) {
          const trend = this.calculateSimpleTrend(dataset.data);
          if (Math.abs(trend) > 1) {
            const direction = trend > 0 ? "improving" : "declining";
            return [
              `Overall trend: ${direction} (${Math.abs(trend).toFixed(1)}%)`,
            ];
          }
        }
      }
      return [];
    },
  },
};
</script>

<style scoped>
canvas {
  max-height: 500px;
}
</style>
